import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { IoInformationCircleOutline, IoMailOpenOutline } from 'react-icons/io5'
import heroBackdrop from '../../assets/pixe.png'
import { resendVerificationCode, verifyEmailCode, extractAuthError } from '../../api/auth'

const DIGIT_COUNT = 6

export default function Verify() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const storedVerificationId =
    typeof window !== 'undefined' ? window.sessionStorage.getItem('pixeloop_verification_id') || '' : ''
  const storedEmail =
    typeof window !== 'undefined' ? window.sessionStorage.getItem('pixeloop_verification_email') || '' : ''
  const storedCooldown =
    typeof window !== 'undefined' ? window.sessionStorage.getItem('pixeloop_verification_cooldown') || '' : ''
  const email = params.get('email') || storedEmail || ''
  const nextRoute = params.get('next') || '/onboarding/handlepicker'
  const verificationId =
    params.get('verificationId') ||
    params.get('verification_id') ||
    params.get('ticket') ||
    storedVerificationId ||
    ''
  const initialCooldown = Math.max(0, Number(params.get('cooldown') || storedCooldown) || 120)
  const initialDevCode = params.get('devCode') || params.get('dev_code') || ''

  const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill(''))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const code = digits.join('')
  const canVerify = digits.every((d) => d.length === 1)
  const [devCode, setDevCode] = useState(initialDevCode)

  const [cooldown, setCooldown] = useState(initialCooldown)
  useEffect(() => {
    setCooldown(initialCooldown)
  }, [initialCooldown])
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => clearInterval(id)
  }, [cooldown])

  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [infoText, setInfoText] = useState('')

  useEffect(() => {
    if (!verificationId) {
      setErrorText('Verification link expired. Please start signup again.')
    }
  }, [verificationId])
  useEffect(() => {
    if (typeof window === 'undefined' || !verificationId) return
    window.sessionStorage.setItem('pixeloop_verification_id', verificationId)
    if (email) window.sessionStorage.setItem('pixeloop_verification_email', email)
  }, [verificationId, email])
  useEffect(() => {
    if (devCode) {
      applyFullCode(devCode)
    }
  }, [devCode])

  const focusInput = (index: number) => {
    inputsRef.current[index]?.focus()
    inputsRef.current[index]?.select()
  }

  const onChangeDigit = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '')
    const value = raw.slice(-1)
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
    if (value && index < DIGIT_COUNT - 1) {
      focusInput(index + 1)
    }
  }

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1)
    }
  }

  const applyFullCode = (raw: string) => {
    const clean = raw.replace(/\D/g, '').slice(0, DIGIT_COUNT)
    setDigits(() => {
      const next = Array(DIGIT_COUNT).fill('')
      for (let i = 0; i < clean.length; i += 1) next[i] = clean[i]
      return next
    })
    if (clean.length) {
      setTimeout(() => focusInput(Math.min(clean.length, DIGIT_COUNT) - 1), 0)
    }
  }

  const applySnippetAt = (start: number, snippet: string) => {
    const clean = snippet.replace(/\D/g, '')
    if (!clean) return
    setDigits((prev) => {
      const next = [...prev]
      let cursor = start
      for (const ch of clean) {
        if (cursor >= DIGIT_COUNT) break
        next[cursor] = ch
        cursor += 1
      }
      return next
    })
    setTimeout(() => focusInput(Math.min(DIGIT_COUNT - 1, start + clean.length - 1)), 0)
  }

  const onPasteDigit = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    const payload = event.clipboardData?.getData('text') || ''
    const cleaned = payload.replace(/\D/g, '')
    if (!cleaned) return
    event.preventDefault()
    if (index === 0) applyFullCode(cleaned)
    else applySnippetAt(index, cleaned)
  }

  const verifyCode = async () => {
    if (!canVerify || !verificationId || verifying) return
    setVerifying(true)
    setErrorText('')
    setInfoText('')
    try {
      await verifyEmailCode({ verification_id: verificationId, code })
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('pixeloop_verification_id')
        window.sessionStorage.removeItem('pixeloop_verification_email')
        window.sessionStorage.removeItem('pixeloop_verification_cooldown')
      }
      navigate(nextRoute, { replace: true })
    } catch (err) {
      setErrorText(extractAuthError(err as any))
    } finally {
      setVerifying(false)
    }
  }

  const resend = async () => {
    if (cooldown > 0 || !verificationId || resending) return
    setResending(true)
    setErrorText('')
    setInfoText('')
    try {
      const ticket = await resendVerificationCode(verificationId)
      const nextCooldown = ticket.resend_cooldown_seconds || 120
      setCooldown(nextCooldown)
      setDigits(Array(DIGIT_COUNT).fill(''))
      focusInput(0)
      setInfoText(`Sent a new code to ${email || 'your email'}.`)
      setDevCode(ticket.dev_code || '')
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('pixeloop_verification_cooldown', String(nextCooldown))
      }
    } catch (err) {
      setErrorText(extractAuthError(err as any))
    } finally {
      setResending(false)
    }
  }

  const openMail = () => {
    if (typeof window !== 'undefined') {
      window.location.href = 'mailto:'
    }
  }

  const changeEmail = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('pixeloop_verification_id')
      window.sessionStorage.removeItem('pixeloop_verification_email')
      window.sessionStorage.removeItem('pixeloop_verification_cooldown')
    }
    navigate('/signup')
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0b0b0b] via-[#070707] to-black text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <img src={heroBackdrop} alt="" className="h-full w-full object-cover blur-3xl" />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative mx-auto w-full max-w-5xl px-6 pb-16 pt-16">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <section className="space-y-6 text-center lg:text-left">
            <div className="space-y-3">
              <h1 className="text-3xl font-black md:text-4xl">We’ve emailed you a code</h1>
              <p className="text-base text-white/75 md:text-lg">
                Enter the 6 digits we sent to <span className="font-semibold text-white">{email || 'your email'}</span>{' '}
                to keep onboarding. This keeps bots out of the handle market.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-left text-sm text-white/75 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
              <p className="font-semibold uppercase tracking-[0.3em] text-white/50">Tips</p>
              <ul className="mt-3 list-disc space-y-1 pl-5">
                <li>Codes expire in about 2 minutes—enter it as soon as it arrives.</li>
                <li>Check spam/promotions if you don’t see it in a minute.</li>
                <li>Add support@pixeloop.com to contacts for faster delivery.</li>
              </ul>
              <button
                type="button"
                onClick={openMail}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:text-white"
              >
                <IoMailOpenOutline />
                Open mail app
              </button>
            </div>
            <p className="text-sm text-white/70">
              Wrong inbox?{' '}
              <button type="button" onClick={changeEmail} className="font-semibold text-white hover:underline">
                Change email
              </button>
            </p>
          </section>

          <section className="rounded-[32px] border border-white/10 bg-white/5 p-8 text-center shadow-[0_35px_100px_rgba(0,0,0,0.55)]">
            <h2 className="text-3xl font-black">Verify your email</h2>
            <p className="mt-2 text-sm text-white/70">Enter the 6-digit code to keep going.</p>

            <div className="mt-8 flex justify-center gap-3">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputsRef.current[index] = el
                  }}
                  value={digit}
                  onChange={(e) => onChangeDigit(index, e)}
                  onPaste={(e) => onPasteDigit(index, e)}
                  onKeyDown={(e) => onKeyDown(index, e)}
                  inputMode="numeric"
                  maxLength={1}
                  className="h-14 w-12 rounded-2xl border border-white/15 bg-white/5 text-center text-2xl font-bold text-white outline-none focus:border-white focus:ring-2 focus:ring-pink-400/60"
                />
              ))}
            </div>

            {devCode && (
              <div className="mt-6 rounded-2xl border border-white/15 bg-black/25 p-4 text-sm text-white/80">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Verification code</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="rounded-lg border border-white/15 bg-black/40 px-4 py-2 text-lg font-semibold tracking-[0.5em]">
                    {devCode}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof navigator !== 'undefined' && navigator?.clipboard) {
                        navigator.clipboard.writeText(devCode).catch(() => {})
                      }
                    }}
                    className="rounded-xl border border-white/20 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
                  >
                    Copy
                  </button>
                </div>
                <p className="mt-2 text-xs text-white/60">
                  Temporary helper — remove before launch. Paste into the boxes above if auto-fill fails.
                </p>
              </div>
            )}

            <GradientButton onClick={verifyCode} disabled={!canVerify || !verificationId || verifying}>
              {verifying ? 'Verifying…' : 'Verify & Continue'}
            </GradientButton>
            {errorText && <p className="mt-3 text-sm text-rose-300">{errorText}</p>}
            {!errorText && infoText && <p className="mt-3 text-sm text-white/70">{infoText}</p>}

            <div className="mt-6 flex flex-col items-center gap-2 text-sm text-white/70 lg:flex-row lg:justify-center">
              <span>Didn’t get a code?</span>
              <button
                type="button"
                disabled={cooldown > 0 || resending || !verificationId}
                onClick={resend}
                className="font-semibold text-white hover:text-white/80 disabled:opacity-60"
              >
                {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend now'}
              </button>
            </div>

            <div className="mt-6 flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left text-sm text-white/70">
              <IoInformationCircleOutline className="text-white/70" />
              <span>Still stuck? forward the email to support@pixeloop.com and we’ll help manually.</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function GradientButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mt-6 w-full rounded-2xl bg-[#1d4ed8] px-6 py-3 text-base font-bold text-white shadow-[0_15px_35px_rgba(0,0,0,0.45)] transition hover:opacity-95 disabled:opacity-60"
    >
      {children}
    </button>
  )
}
